import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { exportLeads } from '../../lib/api';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuRadioGroup, DropdownMenuRadioItem } from '../ui/dropdown-menu';
import { Zap, Upload, Plus, Sun, Moon, User, LogOut, Palette, Type, Bell, Users, Download, Shield, Crown } from 'lucide-react';

export const Navbar = ({ user, onImportClick, onAddClick, onSettingsClick, onAdminClick }) => {
  const { logout, updateTheme } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleThemeChange = async (theme) => {
    await updateTheme(theme, user?.accent_color || 'cyan', user?.font_theme || 'modern');
  };

  const handleAccentChange = async (accent) => {
    await updateTheme(user?.theme || 'dark', accent, user?.font_theme || 'modern');
  };

  const handleFontChange = async (font) => {
    await updateTheme(user?.theme || 'dark', user?.accent_color || 'cyan', font);
  };

  const handleExport = () => {
    exportLeads();
  };

  return (
    <nav className="h-14 border-b flex items-center px-4 justify-between glass-nav sticky top-0 z-50" data-testid="navbar">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center animate-glow">
          <Zap className="w-5 h-5 text-accent-foreground" />
        </div>
        <div>
          <h1 className="font-heading text-lg font-bold tracking-tight leading-none">Commander OS</h1>
          <p className="text-xs text-muted-foreground">Sales CRM</p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {user?.role !== 'superadmin' && (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExport}
              className="hidden md:flex"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={onImportClick}
              className="hidden sm:flex"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>

            <Button 
              size="sm" 
              onClick={onAddClick}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                {user?.role === 'superadmin' ? (
                  <Shield className="w-4 h-4 text-yellow-500" />
                ) : user?.role === 'admin' ? (
                  <Shield className="w-4 h-4 text-accent" />
                ) : (
                  <User className="w-4 h-4 text-accent" />
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span>{user?.name}</span>
                  {user?.role === 'superadmin' && (
                    <span className="text-[10px] bg-yellow-500 text-black px-1.5 py-0.5 rounded font-bold">SUPER ADMIN</span>
                  )}
                  {user?.role === 'admin' && (
                    <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 rounded">ADMIN</span>
                  )}
                </div>
                <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {user?.role === 'admin' && (
              <>
                <DropdownMenuItem onClick={onAdminClick} data-testid="admin-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Team Management
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="theme-submenu">
                {user?.theme === 'dark' ? <Moon className="w-4 h-4 mr-2" /> : <Sun className="w-4 h-4 mr-2" />}
                Mode
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={user?.theme || 'dark'} onValueChange={handleThemeChange}>
                  <DropdownMenuRadioItem value="light" data-testid="theme-light">
                    <Sun className="w-4 h-4 mr-2" /> Light
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="dark" data-testid="theme-dark">
                    <Moon className="w-4 h-4 mr-2" /> Dark
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="accent-submenu">
                <Palette className="w-4 h-4 mr-2" />
                Color Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                <DropdownMenuRadioGroup value={user?.accent_color || 'cyan'} onValueChange={handleAccentChange}>
                  <DropdownMenuRadioItem value="cyan" data-testid="accent-cyan">
                    <div className="w-4 h-4 rounded-full bg-cyan-500 mr-2" /> Cyber Cyan
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="lime" data-testid="accent-lime">
                    <div className="w-4 h-4 rounded-full bg-lime-400 mr-2" /> Neon Lime
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="pink" data-testid="accent-pink">
                    <div className="w-4 h-4 rounded-full bg-pink-500 mr-2" /> Hot Pink
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="orange" data-testid="accent-orange">
                    <div className="w-4 h-4 rounded-full bg-orange-500 mr-2" /> Fire Orange
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="violet" data-testid="accent-violet">
                    <div className="w-4 h-4 rounded-full bg-violet-500 mr-2" /> Electric Violet
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="emerald" data-testid="accent-emerald">
                    <div className="w-4 h-4 rounded-full bg-emerald-500 mr-2" /> Matrix Green
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="red" data-testid="accent-red">
                    <div className="w-4 h-4 rounded-full bg-red-500 mr-2" /> Blood Red
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="gold" data-testid="accent-gold">
                    <div className="w-4 h-4 rounded-full bg-amber-400 mr-2" /> Gold Rush
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="font-submenu">
                <Type className="w-4 h-4 mr-2" />
                Font Style
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-48">
                <DropdownMenuRadioGroup value={user?.font_theme || 'modern'} onValueChange={handleFontChange}>
                  <DropdownMenuRadioItem value="modern" data-testid="font-modern">
                    <span className="font-sans mr-2">Aa</span> Modern (Inter)
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="mono" data-testid="font-mono">
                    <span className="font-mono mr-2">Aa</span> Terminal (Mono)
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="elegant" data-testid="font-elegant">
                    <span className="mr-2" style={{fontFamily: 'Playfair Display'}}>Aa</span> Elegant
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="bold" data-testid="font-bold">
                    <span className="font-black mr-2">Aa</span> Bold (Chivo)
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="tech" data-testid="font-tech">
                    <span className="mr-2" style={{fontFamily: 'Space Grotesk'}}>Aa</span> Tech
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSettingsClick} data-testid="notifications-btn">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};
